import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { adminApi, type SoftwareVendor, type VendorSearchField } from '@/api/admin';
import { INDUSTRY_OPTIONS, VENDOR_SEARCH_FIELD_OPTIONS } from '@/constants/options';
import { STATES, citiesOf } from '@/constants/india';
import { PageHeader, PaginationFooter } from '@/components';
import { AdminSearchWithFieldSelect } from '@/components/AdminSearchWithFieldSelect';
import { brand } from '@/theme/theme';

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

type SortCol = 'companyName' | 'softwareName' | 'city' | 'state' | 'phone' | 'industry' | 'supplierCount' | 'retailerCount';
type SortDir = 'asc' | 'desc';

function sortRows(rows: SoftwareVendor[], col: SortCol, dir: SortDir): SoftwareVendor[] {
  return [...rows].sort((a, b) => {
    let c = 0;
    if (col === 'supplierCount' || col === 'retailerCount') {
      c = (a[col] ?? 0) - (b[col] ?? 0);
    } else {
      const av = (a[col as keyof SoftwareVendor] ?? '') as string;
      const bv = (b[col as keyof SoftwareVendor] ?? '') as string;
      c = av.localeCompare(bv, undefined, { sensitivity: 'base' });
    }
    return dir === 'asc' ? c : -c;
  });
}

function industryLabel(value?: string | null): string {
  if (!value) return '—';
  return INDUSTRY_OPTIONS.find(o => o.value === value)?.label ?? value;
}

// ---------------------------------------------------------------------------
// Shared vendor form fields (used in both Add and Edit dialogs)
// ---------------------------------------------------------------------------

interface VendorFormState {
  companyName: string;
  softwareName: string;
  phone: string;
  gstin: string;
  city: string;
  state: string;
  industry: string;
}

const emptyForm = (): VendorFormState => ({
  companyName: '', softwareName: '', phone: '', gstin: '', city: '', state: '', industry: '',
});

function VendorFormFields({
  form,
  onChange,
}: {
  form: VendorFormState;
  onChange: (patch: Partial<VendorFormState>) => void;
}) {
  const cityOptions = useMemo(() => citiesOf(form.state), [form.state]);

  const field = (
    label: string,
    key: keyof VendorFormState,
    required = false,
  ) => (
    <TextField
      label={label + (required ? ' *' : '')}
      size="small" fullWidth
      value={form[key]}
      onChange={e => onChange({ [key]: e.target.value })}
      sx={{ mb: 1.5 }}
    />
  );

  return (
    <>
      {field('Company Name',  'companyName',  true)}
      {field('Software Name', 'softwareName', true)}
      {/* Industry dropdown */}
      <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
        <InputLabel>Industry</InputLabel>
        <Select value={form.industry} label="Industry"
          onChange={e => onChange({ industry: e.target.value })}>
          <MenuItem value=""><em>None</em></MenuItem>
          {INDUSTRY_OPTIONS.map(o => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {field('Phone',  'phone')}
      {field('GST No', 'gstin')}
      {/* State — filterable autocomplete */}
      <Autocomplete
        freeSolo
        autoHighlight
        openOnFocus
        options={STATES}
        value={form.state}
        onChange={(_, val) => onChange({ state: val ?? '', city: '' })}
        onInputChange={(_, val) => onChange({ state: val, city: '' })}
        filterOptions={(opts, { inputValue }) => {
          const q = inputValue.trim().toLowerCase();
          if (!q) return opts.slice(0, 50);
          return opts.filter(o => o.toLowerCase().includes(q)).slice(0, 50);
        }}
        renderInput={(params) => (
          <TextField {...params} label="State" size="small" fullWidth sx={{ mb: 1.5 }} />
        )}
      />
      {/* City — filtered by selected state */}
      <Autocomplete
        freeSolo
        autoHighlight
        openOnFocus
        disabled={!form.state}
        options={cityOptions}
        value={form.city}
        onChange={(_, val) => onChange({ city: val ?? '' })}
        onInputChange={(_, val) => onChange({ city: val })}
        filterOptions={(opts, { inputValue }) => {
          const q = inputValue.trim().toLowerCase();
          if (!q) return opts.slice(0, 50);
          return opts.filter(o => o.toLowerCase().includes(q)).slice(0, 50);
        }}
        renderInput={(params) => (
          <TextField
            {...params} label="City" size="small" fullWidth sx={{ mb: 1.5 }}
            helperText={!form.state ? 'Choose a state first' : undefined}
          />
        )}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Add Software Vendor dialog
// ---------------------------------------------------------------------------

interface AddDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (v: SoftwareVendor) => void;
}

function AddSoftwareVendorDialog({ open, onClose, onCreated }: AddDialogProps) {
  const [form, setForm] = useState<VendorFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const patch = (p: Partial<VendorFormState>) => setForm(f => ({ ...f, ...p }));

  const handleClose = () => { setForm(emptyForm()); setError(''); onClose(); };

  const handleSave = async () => {
    if (!form.companyName.trim() || !form.softwareName.trim()) {
      setError('Company Name and Software Name are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      const created = await adminApi.createSoftwareVendor({
        companyName:  form.companyName.trim(),
        softwareName: form.softwareName.trim(),
        phone:    form.phone.trim()    || undefined,
        gstin:    form.gstin.trim()    || undefined,
        city:     form.city.trim()     || undefined,
        state:    form.state.trim()    || undefined,
        industry: form.industry        || undefined,
      });
      onCreated(created);
      handleClose();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to create software vendor');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5 }}>Add Software Vendor</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 13 }}>{error}</Alert>}
        <VendorFormFields form={form} onChange={patch} />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button size="small" onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ minWidth: 100 }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit Software Vendor dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  vendor: SoftwareVendor | null;
  onClose: () => void;
  onUpdated: (v: SoftwareVendor) => void;
}

function EditSoftwareVendorDialog({ vendor, onClose, onUpdated }: EditDialogProps) {
  const [form, setForm] = useState<VendorFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    if (vendor) {
      setForm({
        companyName:  vendor.companyName  ?? '',
        softwareName: vendor.softwareName ?? '',
        phone:    vendor.phone    ?? '',
        gstin:    vendor.gstin    ?? '',
        city:     vendor.city     ?? '',
        state:    vendor.state    ?? '',
        industry: vendor.industry ?? '',
      });
      setError('');
    }
  }, [vendor]);

  const patch = (p: Partial<VendorFormState>) => setForm(f => ({ ...f, ...p }));

  if (!vendor) return null;

  const handleSave = async () => {
    if (!form.companyName.trim() || !form.softwareName.trim()) {
      setError('Company Name and Software Name are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      const updated = await adminApi.updateSoftwareVendor(vendor.id, {
        companyName:  form.companyName.trim(),
        softwareName: form.softwareName.trim(),
        phone:    form.phone.trim()    || '',
        gstin:    form.gstin.trim()    || '',
        city:     form.city.trim()     || '',
        state:    form.state.trim()    || '',
        industry: form.industry        || '',
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to update software vendor');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!vendor} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5 }}>Edit Software Vendor</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 13 }}>{error}</Alert>}
        <VendorFormFields form={form} onChange={patch} />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button size="small" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ minWidth: 100 }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminVendorsPage() {
  const [vendors,  setVendors]  = useState<SoftwareVendor[]>([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [addOpen,  setAddOpen]  = useState(false);
  const [editing,  setEditing]  = useState<SoftwareVendor | null>(null);

  // Search — explicit icon/Enter trigger, never live-as-you-type; resolved
  // entirely server-side, not by filtering the already-fetched vendor list.
  const [searchInput, setSearchInput] = useState('');
  const [searchField, setSearchField] = useState<VendorSearchField>('name');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState<VendorSearchField>('name');

  // Pagination
  const [page, setPage] = useState(0);
  const [size] = useState(50);

  // Sort
  const [sortCol, setSortCol] = useState<SortCol>('companyName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const load = async (query = appliedQuery, field = appliedSearchField, p = page) => {
    setLoading(true); setError('');
    try {
      const res = await adminApi.listSoftwareVendors({
        q: query || undefined, searchField: query ? field : undefined, page: p, size,
      });
      setVendors(res.content);
      setTotal(res.totalElements);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to load vendors');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    const query = searchInput.trim();
    setAppliedQuery(query);
    setAppliedSearchField(searchField);
    setPage(0);
    load(query, searchField, 0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    load(appliedQuery, appliedSearchField, newPage);
  };

  const handleSort = (col: SortCol) => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortTH = ({ col, label, sx }: { col: SortCol; label: string; sx?: object }) => (
    <TableCell sx={{ fontWeight: 700, fontSize: 12, ...sx }}>
      <TableSortLabel active={sortCol === col} direction={sortCol === col ? sortDir : 'asc'}
        onClick={() => handleSort(col)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // `vendors` is already filtered server-side by the applied search — only
  // sorting (a pure display concern) happens here.
  const rows = sortRows(vendors, sortCol, sortDir);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="Software Vendors"
        subtitle={`${total} software vendor${total !== 1 ? 's' : ''} registered`}
        actions={
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}>
            Add Software Vendor
          </Button>
        }
      />

      {/* Search + refresh */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <AdminSearchWithFieldSelect
          field={searchField}
          onFieldChange={v => setSearchField(v as VendorSearchField)}
          options={VENDOR_SEARCH_FIELD_OPTIONS}
          value={searchInput}
          onChangeText={setSearchInput}
          onSearch={handleSearch}
        />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => load()} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && vendors.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, width: 40 }}>#</TableCell>
                <SortTH col="companyName"  label="Vendor Name"    sx={{ minWidth: 200 }} />
                <SortTH col="softwareName" label="Software Name"  sx={{ width: 150 }} />
                <SortTH col="industry"     label="Industry"       sx={{ width: 150 }} />
                <SortTH col="city"         label="Location"       sx={{ width: 160 }} />
                <TableCell sx={{ fontWeight: 700, fontSize: 12, width: 140 }}>GST No</TableCell>
                <SortTH col="phone"        label="Phone"          sx={{ width: 120 }} />
                <SortTH col="supplierCount"  label="Suppliers"    sx={{ width: 80 }} />
                <SortTH col="retailerCount"  label="Retailers"    sx={{ width: 80 }} />
                <TableCell sx={{ fontWeight: 700, fontSize: 12, width: 60 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6, color: brand.inkSoft }}>
                    {vendors.length === 0
                      ? 'No software vendors yet. Click "Add Software Vendor" to create one.'
                      : 'No vendors match your search.'}
                  </TableCell>
                </TableRow>
              ) : rows.map((v, idx) => {
                const locationLine = [v.city, v.state].filter(Boolean).join(', ');
                return (
                  <TableRow key={v.id} hover
                    sx={{ '&:nth-of-type(even)': { bgcolor: '#fbfbfd' } }}>
                    <TableCell sx={{ fontSize: 12, color: brand.inkSoft }}>{idx + 1}</TableCell>

                    {/* Vendor Name */}
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy }}>
                        {v.companyName}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ fontSize: 12, color: brand.inkSoft }}>{v.softwareName}</TableCell>

                    {/* Industry */}
                    <TableCell sx={{ fontSize: 12 }}>{industryLabel(v.industry)}</TableCell>

                    {/* Location: city + state stacked */}
                    <TableCell>
                      {locationLine ? (
                        <>
                          <Typography sx={{ fontSize: 12 }}>{v.city ?? ''}</Typography>
                          {v.state && (
                            <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{v.state}</Typography>
                          )}
                        </>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ fontSize: 12 }}>{v.gstin ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.phone ?? '—'}</TableCell>

                    {/* Supplier count */}
                    <TableCell align="center">
                      <Typography sx={{
                        fontSize: 12, fontWeight: v.supplierCount > 0 ? 700 : 400,
                        color: v.supplierCount > 0 ? brand.navy : '#9ca3af',
                      }}>
                        {v.supplierCount}
                      </Typography>
                    </TableCell>

                    {/* Retailer count */}
                    <TableCell align="center">
                      <Typography sx={{
                        fontSize: 12, fontWeight: v.retailerCount > 0 ? 700 : 400,
                        color: v.retailerCount > 0 ? brand.navy : '#9ca3af',
                      }}>
                        {v.retailerCount}
                      </Typography>
                    </TableCell>

                    {/* Edit action */}
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setEditing(v)}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PaginationFooter
        page={page}
        totalPages={Math.max(1, Math.ceil(total / size))}
        total={total}
        rowsPerPage={size}
        rowsPerPageOptions={[size]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={() => {}}
        label="vendors"
      />

      <AddSoftwareVendorDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => load(appliedQuery, appliedSearchField, page)}
      />

      <EditSoftwareVendorDialog
        vendor={editing}
        onClose={() => setEditing(null)}
        onUpdated={updated => setVendors(prev => prev.map(v => v.id === updated.id ? updated : v))}
      />
    </Box>
  );
}
